import { z } from "zod";

export const MAX_BASE_VALUE = 2 ** 32; // 4,294,967,296
export const MAX_PERCENTAGE_VALUE = 2 ** 32;

const createNumberSchema = (max: number, label: string) => {
	return z
		.number()
		.min(0, `${label}は0以上にしてください`)
		.max(max, `${label}が上限を超えています`);
};

export const DamageReductionPercentageSchema = z
	.number()
	.int("ダメージ軽減率は整数で入力してください")
	.min(0, "ダメージ軽減率は0以上にしてください")
	.max(100, "ダメージ軽減率は100以下にしてください")
	.brand<"DamageReductionPercentage">();

export type DamageReductionPercentage = z.infer<
	typeof DamageReductionPercentageSchema
>;

export const DokkanStatsSchema = z.object({
	// 基礎ステータス
	stats_atk: createNumberSchema(MAX_BASE_VALUE, "ATK"),
	stats_def: createNumberSchema(MAX_BASE_VALUE, "DEF"),

	// リーダースキル
	Lskill_atk_1: createNumberSchema(MAX_PERCENTAGE_VALUE, "自前リーダーATK"),
	Lskill_def_1: createNumberSchema(MAX_PERCENTAGE_VALUE, "自前リーダーDEF"),
	Lskill_atk_2: createNumberSchema(MAX_PERCENTAGE_VALUE, "フレンドリーダーATK"),
	Lskill_def_2: createNumberSchema(MAX_PERCENTAGE_VALUE, "フレンドリーダーDEF"),

	// パッシブスキル
	Pskill_atk_1: createNumberSchema(MAX_PERCENTAGE_VALUE, "パッシブ①ATK"),
	Pskill_def_1: createNumberSchema(MAX_PERCENTAGE_VALUE, "パッシブ①DEF"),
	Pskill_atk_2: createNumberSchema(MAX_PERCENTAGE_VALUE, "パッシブ②ATK"),
	Pskill_def_2: createNumberSchema(MAX_PERCENTAGE_VALUE, "パッシブ②DEF"),

	// サポート効果
	support_atk_1: createNumberSchema(MAX_PERCENTAGE_VALUE, "パッシブ①ATK"),
	support_def_1: createNumberSchema(MAX_PERCENTAGE_VALUE, "パッシブ①DEF"),
	support_atk_2: createNumberSchema(MAX_PERCENTAGE_VALUE, "パッシブ②ATK"),
	support_def_2: createNumberSchema(MAX_PERCENTAGE_VALUE, "パッシブ②DEF"),
	support_atk_active: createNumberSchema(
		MAX_PERCENTAGE_VALUE,
		"アクティブサポートATK",
	),
	support_def_active: createNumberSchema(
		MAX_PERCENTAGE_VALUE,
		"アクティブサポートDEF",
	),
	support_atk_field: createNumberSchema(MAX_PERCENTAGE_VALUE, "フィールドATK"),
	support_def_field: createNumberSchema(MAX_PERCENTAGE_VALUE, "フィールドDEF"),
	support_atk_memory: createNumberSchema(MAX_PERCENTAGE_VALUE, "メモリーATK"),
	support_def_memory: createNumberSchema(MAX_PERCENTAGE_VALUE, "メモリーDEF"),
	support_atk_item: createNumberSchema(MAX_PERCENTAGE_VALUE, "アイテムATK"),
	support_def_item: createNumberSchema(MAX_PERCENTAGE_VALUE, "アイテムDEF"),

	// 必殺技
	SA_atk: createNumberSchema(MAX_PERCENTAGE_VALUE, "必殺追加効果ATK"),
	SA_def: createNumberSchema(MAX_PERCENTAGE_VALUE, "必殺追加効果DEF"),
	SA_power: z.number(), // バリデーションなし
	SAboost_level: z
		.number()
		.int("整数で入力してください")
		.min(0, "0以上にしてください")
		.max(50, "必殺威力アップレベルは50以下にしてください"),

	// その他
	link_atk: createNumberSchema(MAX_PERCENTAGE_VALUE, "リンクスキルATK"),
	link_def: createNumberSchema(MAX_PERCENTAGE_VALUE, "リンクスキルDEF"),
	ki_bonus: z
		.number()
		.min(0.1, "気力ボーナスを選択してください")
		.max(2.0, "気力ボーナスを選択してください"),
});

export type DokkanStats = z.infer<typeof DokkanStatsSchema>;

export const CalculationResultSchema = z.object({
	atk: z.number(),
	def: z.number(),
});

export type CalculationResult = z.infer<typeof CalculationResultSchema>;

export const DamageCalculationInputSchema = z.object({
	enemy_atk: createNumberSchema(MAX_BASE_VALUE, "敵ATK"),
	damage_reduction: DamageReductionPercentageSchema,
	player_def: z.number().min(0),
});

export type DamageCalculationInput = z.infer<
	typeof DamageCalculationInputSchema
>;

export const DamageCalculationResultSchema = z.object({
	damage_taken: z.number(),
	damage_reduction_percentage: z.number(),
});

export type DamageCalculationResult = z.infer<
	typeof DamageCalculationResultSchema
>;

export const FullCalculationResultSchema = z.object({
	atk: z.number().int(),
	def: z.number().int(),
	atkOverflow: z.boolean().optional(),
	defOverflow: z.boolean().optional(),
	damage_calculation: DamageCalculationResultSchema.optional(),
});

export type FullCalculationResult = z.infer<typeof FullCalculationResultSchema>;

export type ValidationError = {
	field: string;
	message: string;
};

export function validateDokkanStats(
	stats: Partial<DokkanStats>,
	dagameCalcInput?: Partial<z.input<typeof DamageCalculationInputSchema>>,
): ValidationError[] {
	const dokkanStatsResult = DokkanStatsSchema.partial().safeParse(stats);
	const damageCalcResult =
		DamageCalculationInputSchema.partial().safeParse(dagameCalcInput);
	const errors: ValidationError[] = [];

	if (!dokkanStatsResult.success && dokkanStatsResult.error) {
		errors.push(
			...dokkanStatsResult.error.issues.map((err) => ({
				field: err.path.join("."),
				message: err.message,
			})),
		);
	}
	if (damageCalcResult && !damageCalcResult.success && damageCalcResult.error) {
		errors.push(
			...damageCalcResult.error.issues.map((err) => ({
				field: err.path.join("."),
				message: err.message,
			})),
		);
	}

	return errors;
}
